import React from 'react';
import { Box, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';

interface StarRatingProps {
  rating: number;
  totalStars?: number;
  size?: 'small' | 'medium' | 'large';
  interactive?: boolean;
  onChange?: (rating: number) => void;
  readonly?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  totalStars = 5,
  size = 'medium',
  interactive = false,
  onChange,
  readonly = false
}) => {
  const [hoverRating, setHoverRating] = React.useState<number | null>(null);

  const getSizeStyle = () => {
    switch (size) {
      case 'small':
        return { fontSize: '1rem' };
      case 'large':
        return { fontSize: '2rem' };
      default:
        return { fontSize: '1.5rem' };
    }
  };

  const handleClick = (value: number) => {
    if (interactive && !readonly && onChange) {
      onChange(value);
    }
  };

  const handleMouseEnter = (value: number) => {
    if (interactive && !readonly) {
      setHoverRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (interactive && !readonly) {
      setHoverRating(null);
    }
  };

  const renderStar = (position: number) => {
    const effectiveRating = hoverRating !== null ? hoverRating : rating;
    
    if (interactive) {
      return (
        <IconButton
          key={position}
          onClick={() => handleClick(position)}
          onMouseEnter={() => handleMouseEnter(position)}
          onMouseLeave={handleMouseLeave}
          disabled={readonly}
          sx={{ 
            padding: 0.5,
            color: position <= effectiveRating ? '#FFB400' : '#E0E0E0'
          }}
        >
          {position <= effectiveRating ? (
            <StarIcon sx={getSizeStyle()} />
          ) : (
            <StarBorderIcon sx={getSizeStyle()} />
          )}
        </IconButton>
      );
    }

    // Non-interactive display
    const filled = position <= Math.floor(rating);
    const halfFilled = position === Math.ceil(rating) && rating % 1 !== 0;

    return (
      <Box key={position} component="span" sx={{ color: '#FFB400' }}>
        {filled ? (
          <StarIcon sx={getSizeStyle()} />
        ) : halfFilled ? (
          <StarHalfIcon sx={getSizeStyle()} />
        ) : (
          <StarBorderIcon sx={{ ...getSizeStyle(), color: '#E0E0E0' }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {Array.from({ length: totalStars }, (_, i) => i + 1).map(renderStar)}
    </Box>
  );
};

export default StarRating;